import { useState } from "react";
import Dialog from "../../../../../components/Dialog/SideDrawer";
import EditAddress from "./editAddressDialog";
import { useAppSelector, useAppDispatch } from "../../../../../redux";
import { store } from "../../../../../redux";
import { CUSTOMER_STORE_NAME, ORDER_STORE_NAME } from "../../../../../contants/stores";
import { getSavedAddressesById } from "../../../../customer/actions/customer.actions";
import { saveMultiStopAddresses } from "../../../actions/order.actions";
import { actions as orderActions } from "../../../stores/order.store";
import Modal from "../../../../../components/Dialog/Modal";
import { deleteStopFromCourier, savePaymentDetails, callOrderETAService } from "../../../services/order.services";
import { addWalletEntry } from "../../../../customer/actions/customer.actions";
import toast from "react-hot-toast";

export default function AddressDetailsBox(props: any) {
  const address = props?.address;
  const isMultiStopOrder = props?.isMultiStopOrder || false;
  
  // Get order from Redux store to ensure we have the latest data
  const orderFromRedux = useAppSelector(state => {
    const orders = state[ORDER_STORE_NAME].orders;
    const order = orders.find((o: any) => o.id === props?.orderId);
    return order || state[ORDER_STORE_NAME].searchOrder;
  });
  
  // Use Redux order data if available, otherwise fall back to props
  const deliveryStops = orderFromRedux?.deliveryStops || props?.deliveryStops || [];
  const dropoffStatuses = orderFromRedux?.deliveryInfo?.dropoff_statuses || props?.dropoffStatuses || [];
  const primaryStatus = orderFromRedux?.primaryStatus || props?.primaryStatus;
  const title = props?.type === "pickup" ? "Pickup from" : "Delivery to";
  
  // Helper function to get primaryStatus as a number
  const getPrimaryStatusNumber = (status: any): number => {
    if (typeof status === 'number') return status;
    if (typeof status === 'string') {
      // Map status labels to numbers
      const statusLabelMap: Record<string, number> = {
        'PENDING': 0,
        'ORDER_PLACED': 0,
        'ORDER_CONFIRMED': 10,
        'LOOKING_FOR_DRIVER': 20,
        'DRIVER_ASSIGNED': 30,
        'DRIVER_ASSIGNED_QUEUED': 35,
        'REASSIGNING_DRIVER': 40,
        'DRIVER_ACCEPTED': 50,
        'DRIVER_GOING_TO_PICKUP': 60,
        'DRIVER_AT_PICKUP': 70,
        'ORDER_PICKED_UP': 80,
        'ON_THE_WAY': 90,
        'DRIVER_HAS_ARRIVED': 100,
        'DELIVERED': 110,
        'CANCELLED': 120
      };
      return statusLabelMap[status] ?? 999; // Default to 999 if not found (disallow deletion)
    }
    return 999; // Default to 999 if not found (disallow deletion)
  };
  
  const primaryStatusNumber = getPrimaryStatusNumber(primaryStatus);
  const PICKED_UP_STATUS = 80; // Status 80 = "Order Picked Up"
  
  const dispatch = useAppDispatch();
  const [isEditAddressDialogOpen, setAddressDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stopToDelete, setStopToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const saveAddresses = useAppSelector(state => state[CUSTOMER_STORE_NAME].saveAddresses);

  // Parse customer_name field which contains "CustomerName | LocationName"
  const parseCustomerName = (customerName: string) => {
    if (!customerName) return { name: '', location: '' };
    const parts = customerName.split(' | ');
    return {
      name: parts[0]?.trim() || '',
      location: parts[1]?.trim() || ''
    };
  };

  // Map primary_status to display info
  const getStatusInfo = (primaryStatus: number) => {
    const statusMap: Record<number, { label: string; color: string }> = {
      0: { label: 'Order Placed', color: 'gray' },
      10: { label: 'Order Confirmed', color: 'blue' },
      20: { label: 'Looking for Driver', color: 'yellow' },
      30: { label: 'Driver Assigned', color: 'blue' },
      35: { label: 'Driver Assigned (Queued)', color: 'blue' },
      40: { label: 'Reassigning Driver', color: 'yellow' },
      50: { label: 'Driver Accepted', color: 'blue' },
      60: { label: 'Driver Going to Pickup', color: 'blue' },
      70: { label: 'Driver at Pickup', color: 'blue' },
      80: { label: 'Order Picked Up', color: 'blue' },
      90: { label: 'On the Way to You', color: 'purple' },
      100: { label: 'Driver Has Arrived', color: 'purple' },
      110: { label: 'Delivered', color: 'green' },
      120: { label: 'Cancelled', color: 'red' }
    };
    return statusMap[primaryStatus] || { label: 'Unknown', color: 'gray' };
  };

  // Get color classes based on color name
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 text-green-700',
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      red: 'bg-red-100 text-red-700',
      gray: 'bg-gray-100 text-gray-600'
    };
    return colorMap[color] || colorMap.gray;
  };

  // Check if a stop has a job_id
  const hasJobId = (stop: any, index: number): boolean => {
    // Check if stop has job_id directly
    if (stop?.job_id || stop?.jobId) {
      return true;
    }
    // If using dropoffStatuses, check there too
    if (dropoffStatuses.length > 0 && dropoffStatuses[index]) {
      return !!(dropoffStatuses[index]?.job_id || dropoffStatuses[index]?.jobId);
    }
    return false;
  };

  // Check if delete is allowed for a stop (must have job_id, at least 3 stops, and status before Picked Up)
  const canEditStop = (): boolean => {
    // For multi-stop orders, disable edit if status is "Picked Up" or later
    if (isMultiStopOrder && props?.type === "delivery") {
      if (primaryStatusNumber >= PICKED_UP_STATUS) {
        return false;
      }
    }
    // For single orders, always allow edit (no change)
    return true;
  };

  const canDeleteStop = (stop: any, index: number): boolean => {
    // Cannot delete if order status is "Picked Up" (80) or later
    if (primaryStatusNumber >= PICKED_UP_STATUS) {
      return false;
    }
    // Must have job_id
    if (!hasJobId(stop, index)) {
      return false;
    }
    // Check if we're using dropoffStatuses or deliveryStops
    const currentStops = dropoffStatuses.length > 0 ? dropoffStatuses : deliveryStops;
    // Must have at least 3 stops to allow deletion
    if (currentStops.length <= 2) {
      return false;
    }
    return true;
  };
  
  // Get the reason why delete is disabled (for tooltip)
  const getDeleteDisabledReason = (stop: any, index: number): string => {
    if (primaryStatusNumber >= PICKED_UP_STATUS) {
      return "Cannot delete: Order has already been picked up";
    }
    if (!hasJobId(stop, index)) {
      return "Cannot delete: job_id is missing";
    }
    const currentStops = dropoffStatuses.length > 0 ? dropoffStatuses : deliveryStops;
    if (currentStops.length <= 2) {
      return "Cannot delete: A multi-stop order must have at least 2 stops";
    }
    return "Delete stop";
  };

  const handleDeleteClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Safety check: prevent deletion if not allowed
    const currentStops = dropoffStatuses.length > 0 ? dropoffStatuses : deliveryStops;
    const stop = currentStops[index];
    if (!canDeleteStop(stop, index)) {
      toast.error(getDeleteDisabledReason(stop, index));
      return;
    }
    
    setStopToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteStop = async () => {
    if (stopToDelete === null) return;
    
    // Cannot delete if order status is "Picked Up" (80) or later
    if (primaryStatusNumber >= PICKED_UP_STATUS) {
      toast.error("Cannot delete stop. Order has already been picked up.");
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    if (deliveryStops.length <= 2) {
      toast.error("Cannot delete stop. A multi-stop order must have at least 2 stops.");
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    setIsDeleting(true);
    
    // Get the stop to delete from deliveryStops
    const stopToDeleteObj = deliveryStops[stopToDelete];
    
    // Get job_id from deliveryStops stop, or try dropoffStatuses as fallback
    let jobId = stopToDeleteObj?.job_id || stopToDeleteObj?.jobId;
    
    // If no job_id found, try to get it from dropoffStatuses
    if (!jobId && dropoffStatuses.length > 0 && dropoffStatuses[stopToDelete]) {
      jobId = dropoffStatuses[stopToDelete]?.job_id || dropoffStatuses[stopToDelete]?.jobId;
    }
    
    // Also check the original deliveryStops from props in case Redux update lost the job_id
    if (!jobId) {
      const originalStops = props?.deliveryStops;
      if (originalStops && originalStops[stopToDelete]) {
        jobId = originalStops[stopToDelete]?.job_id || originalStops[stopToDelete]?.jobId;
      }
    }
    
    
    // If no jobId, don't delete and return
    if (!jobId) {
      toast.error("Cannot delete stop: job_id is missing.");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    const orderNumberToUse = props?.deliveryNo || props?.orderNo;
    try {
      const apiResponse = await deleteStopFromCourier(orderNumberToUse, jobId);
      
      // If API returned alreadyDeleted flag, show specific message
      if (apiResponse?.alreadyDeleted) {
        toast.success("Stop was already deleted. Updating local data.");
      } else {
        // If no error was thrown, API call succeeded
        toast.success("Stop deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete stop from courier system. Please try again.");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return; // Don't proceed with Firebase update if API call fails
    }
    
    // Filter out the deleted stop from deliveryStops - preserve original structure
    const processedStops = deliveryStops
      .filter((_: any, index: number) => index !== stopToDelete)
      .map((stop: any) => {
        // Preserve the original stop structure, just update addressStr if needed
        let str = '';
        let additionalText = (stop.type === 'HOME' || stop.type === 'STORE') ? 'Landmark' : 'RM';

        str = stop.type ? str.concat(` | Type: ${stop.type}`) : str;
        str = stop.building ? str.concat(` | BLDG: ${stop.building}`) : str;
        str = stop.floorNumber ? str.concat(` | FLR: ${stop.floorNumber}`) : str;
        str = stop.roomNumber ? str.concat(` | ${additionalText}: ${stop.roomNumber}`) : str;
        str = stop.area ? str.concat(` | Area: ${stop.area}`) : str;
        str = stop.block ? str.concat(` | Block: ${stop.block}`) : str;
        str = stop.street ? str.concat(` | Street: ${stop.street}`) : str;
        str = stop.avenue ? str.concat(` | ${stop.avenue}`) : str;

        return {
          ...stop,
          addressStr: str,
          isValid: false
        };
      });

    const orderValidation = props?.orderType !== 'COURIER' ? {
      ...props?.orderValidation,
      deliveryValidate: {
        ...props?.orderValidation?.deliveryValidate,
        updated: true
      }
    } : {};

    // Filter dropoff_statuses to match the remaining stops by job_id
    let updatedDropoffStatuses = undefined;
    if (dropoffStatuses.length > 0) {
      const remainingJobIds = new Set(processedStops.map((stop: any) => stop.job_id || stop.jobId).filter(Boolean));
      updatedDropoffStatuses = dropoffStatuses.filter((status: any) => 
        remainingJobIds.has(status.job_id || status.jobId)
      );
    }

    // Save the updated stops and dropoff_statuses
    try {
      await saveMultiStopAddresses(
        props?.orderId,
        props?.orderNo,
        processedStops,
        props?.fleetId,
        props?.orderType,
        orderValidation,
        undefined, // changedStopIndices
        updatedDropoffStatuses // dropoffStatuses
      );
    } catch (error) {
      toast.error("Failed to save changes to Firebase");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }

    const currentOrder = store.getState()[ORDER_STORE_NAME].orders.find((order: any) => order.id === props?.orderId);
    if (currentOrder) {
      // Create updated order object with new deliveryStops
      let updatedDropoffStatuses = undefined;
      if (currentOrder.deliveryInfo?.dropoff_statuses && currentOrder.deliveryInfo.dropoff_statuses.length > 0) {
        const remainingJobIds = new Set(processedStops.map((stop: any) => stop.job_id || stop.jobId).filter(Boolean));
        updatedDropoffStatuses = currentOrder.deliveryInfo.dropoff_statuses.filter((status: any) => 
          remainingJobIds.has(status.job_id || status.jobId)
        );
      }
      
      const updatedDeliveryInfo = currentOrder.deliveryInfo ? {
        ...currentOrder.deliveryInfo,
        ...(updatedDropoffStatuses !== undefined ? { dropoff_statuses: updatedDropoffStatuses } : {})
      } : undefined;
      
      const updatedOrder = {
        ...currentOrder,
        deliveryStops: [...processedStops],
        isMultiStopOrder: processedStops.length > 1,
        ...(updatedDeliveryInfo ? { deliveryInfo: { ...updatedDeliveryInfo } } : {})
      };
      
      dispatch(orderActions.updateOrder({
        id: props?.orderId,
        data: updatedOrder
      }));
    }
    
    // Also update searchOrder if needed
    const searchOrder = store.getState()[ORDER_STORE_NAME].searchOrder;
    if (searchOrder && searchOrder.id === props?.orderId) {
      let updatedSearchDropoffStatuses = undefined;
      if (searchOrder.deliveryInfo?.dropoff_statuses && searchOrder.deliveryInfo.dropoff_statuses.length > 0) {
        const remainingJobIds = new Set(processedStops.map((stop: any) => stop.job_id || stop.jobId).filter(Boolean));
        updatedSearchDropoffStatuses = searchOrder.deliveryInfo.dropoff_statuses.filter((status: any) => 
          remainingJobIds.has(status.job_id || status.jobId)
        );
      }
      
      const updatedSearchDeliveryInfo = searchOrder.deliveryInfo ? {
        ...searchOrder.deliveryInfo,
        ...(updatedSearchDropoffStatuses !== undefined ? { dropoff_statuses: updatedSearchDropoffStatuses } : {})
      } : undefined;
      
      dispatch(orderActions.setSearchOrder({
        ...searchOrder,
        deliveryStops: [...processedStops],
        isMultiStopOrder: processedStops.length > 1,
        ...(updatedSearchDeliveryInfo ? { deliveryInfo: { ...updatedSearchDeliveryInfo } } : {})
      }));
    }

    // Recalculate order amounts after stop deletion
    await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to ensure Redux is updated
    
    let orderForRecalculation = store.getState()[ORDER_STORE_NAME].orders.find((order: any) => order.id === props?.orderId) || 
                                 store.getState()[ORDER_STORE_NAME].searchOrder;
    
    if (!orderForRecalculation || !orderForRecalculation.orderInfo?.items || orderForRecalculation.orderInfo.items.length === 0) {
      toast.error("Stop deleted but cannot recalculate amounts (no items found)");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    const lastStop = processedStops.length > 0 ? processedStops[processedStops.length - 1] : null;
    if (!lastStop || !lastStop.location) {
      toast.error("Stop deleted but cannot recalculate amounts (invalid stop location)");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    const lat = lastStop.location._lat || lastStop.location.latitude || 0;
    const lng = lastStop.location._long || lastStop.location.longitude || 0;
    if (lat === 0 && lng === 0) {
      toast.error("Stop deleted but cannot recalculate amounts (invalid coordinates)");
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setStopToDelete(null);
      return;
    }
    
    // Ensure the order has the updated deliveryStops
    orderForRecalculation = {
      ...orderForRecalculation,
      deliveryStops: processedStops,
      isMultiStopOrder: processedStops.length > 1
    };
    
    try {
      // Temporarily update Redux with the order that has updated stops so calculateOrderETA can use it
      dispatch(orderActions.updateOrder({
        id: props?.orderId,
        data: orderForRecalculation
      }));
      
      // Wait a bit more for Redux to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Get original amounts from order
      const oldServiceFee = orderForRecalculation.orderInfo.serviceFee || 0;
      const oldDeliveryFee = orderForRecalculation.orderInfo.deliveryFee || 0;
      const oldDiscount = orderForRecalculation.orderInfo.discount || 0;
      const oldTotalAmount = orderForRecalculation.orderInfo.totalAmount || 0;
      const oldOnlinePaid = orderForRecalculation.orderInfo.onlinePaid || 0;
      const oldWalletPaid = orderForRecalculation.orderInfo.walletPaid || 0;
      
      // Get the stop that's being deleted from deliveryStops
      let deletedStop = deliveryStops[stopToDelete];
      
      // Also check original props if still not found
      if (!deletedStop || !deletedStop.location) {
        const originalStops = props?.deliveryStops;
        if (originalStops && originalStops[stopToDelete]) {
          deletedStop = originalStops[stopToDelete];
        }
      }
      
      if (!deletedStop || !deletedStop.location) {
        toast.error("Stop deleted but cannot calculate amount adjustment (invalid stop location)");
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setStopToDelete(null);
        return;
      }
      
      // Get storeId for ETA calculation
      const storeId = orderForRecalculation.orderType === "BUY"
        ? orderForRecalculation.pickupAddress?.placeId != null
          ? orderForRecalculation.pickupAddress.placeId
          : null
        : orderForRecalculation.orderType === "SELLER"
          ? orderForRecalculation.sellerInfo?.sellerId
          : null;
      
      // Get items for calculating stop amount
      const items = orderForRecalculation.orderInfo.items || [];
      const itemCount = items.filter((item: any) => !item.isCancelled).length || 0;
      const itemTotal = orderForRecalculation.orderInfo.itemTotal || 
        items.filter((item: any) => !item.isCancelled)
          .reduce((acc: number, item: any) => acc + ((item.quantity || 0) * (item.price || 0)), 0);
      
      // Calculate the deleted stop's amount as a single order
      // - First stop: pickup → stop
      // - Other stops: previous stop → current stop (segment)
      const deletedStopLocation = {
        latitude: deletedStop.location._lat || deletedStop.location.latitude || 0,
        longitude: deletedStop.location._long || deletedStop.location.longitude || 0
      };
      
      if (deletedStopLocation.latitude === 0 && deletedStopLocation.longitude === 0) {
        toast.error("Stop deleted but cannot calculate amount adjustment (invalid coordinates)");
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setStopToDelete(null);
        return;
      }
      
      // Determine origin: pickup for first stop, previous stop for others
      let originLocation;
      if (stopToDelete === 0) {
        // First stop: use pickup as origin
        originLocation = {
          latitude: orderForRecalculation.pickupAddress.location._lat,
          longitude: orderForRecalculation.pickupAddress.location._long
        };
      } else {
        // Not first stop: use previous stop as origin from deliveryStops
        let previousStop = deliveryStops[stopToDelete - 1];
        
        // Also check original props if still not found
        if (!previousStop || !previousStop.location) {
          const originalStops = props?.deliveryStops;
          if (originalStops && originalStops[stopToDelete - 1]) {
            previousStop = originalStops[stopToDelete - 1];
          }
        }
        
        if (!previousStop || !previousStop.location) {
          toast.error("Stop deleted but cannot calculate amount adjustment (previous stop invalid)");
          setIsDeleting(false);
          setDeleteConfirmOpen(false);
          setStopToDelete(null);
          return;
        }
        originLocation = {
          latitude: previousStop.location._lat || previousStop.location.latitude || 0,
          longitude: previousStop.location._long || previousStop.location.longitude || 0
        };
      }
      
      // Calculate what the delivery fee would be for this stop segment as a single order
      const deletedStopETAData = await callOrderETAService({
        pickup: originLocation,
        delivery: deletedStopLocation,
        orderType: orderForRecalculation.orderType,
        storeId: storeId,
        itemCount: itemCount,
        itemTotal: itemTotal
      });
      
      if (!deletedStopETAData) {
        toast.error("Stop deleted but cannot calculate amount adjustment");
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setStopToDelete(null);
        return;
      }
      
      // The deleted stop's contribution to the total
      const deletedStopDeliveryFee = deletedStopETAData.deliveryFee || 0;
      const deletedStopServiceFee = deletedStopETAData.serviceFee || 0;
      const deletedStopAmount = deletedStopDeliveryFee + deletedStopServiceFee;
      
      // Subtract the deleted stop's amount from the total
      const newDeliveryFee = oldDeliveryFee - deletedStopDeliveryFee;
      const newServiceFee = oldServiceFee - deletedStopServiceFee;
      const newTotalAmount = oldTotalAmount - deletedStopAmount;
      
      const newCashToCollect = Math.round((newTotalAmount - oldOnlinePaid - oldWalletPaid) * 100) / 100;
      
      const amountDifference = deletedStopAmount;
      
      // Update delivery fee, service fee, discount, and cashToCollect in Firebase
      await savePaymentDetails(
        props?.orderId,
        orderForRecalculation.orderInfo.paymentType || 'CASH',
        newCashToCollect
      );
      
      const { save } = await import("../../../services/order.services");
      const updateData: any = {};
      
      // Update delivery fee and service fee (subtracted deleted stop's fees)
      if (newDeliveryFee !== undefined && newDeliveryFee !== null && newDeliveryFee >= 0) {
        updateData["orderInfo.deliveryFee"] = newDeliveryFee;
      }
      if (newServiceFee !== undefined && newServiceFee !== null && newServiceFee >= 0) {
        updateData["orderInfo.serviceFee"] = newServiceFee;
      }
      if (oldDiscount !== undefined && oldDiscount !== null) {
        updateData["orderInfo.discount"] = oldDiscount;
      }
      if (newTotalAmount !== undefined && newTotalAmount !== null && !isNaN(newTotalAmount) && newTotalAmount >= 0) {
        updateData["orderInfo.totalAmount"] = newTotalAmount;
      }
        
      if (Object.keys(updateData).length > 0) {
        await save(props?.orderId, updateData);
      }
        
        // If customer already paid and the new total is less, refund difference to wallet
        if (amountDifference > 0 && (oldOnlinePaid > 0 || oldWalletPaid > 0)) {
          const customerId = orderForRecalculation.user?.id;
          if (customerId) {
            await addWalletEntry(
              customerId,
              amountDifference,
              `Refund for deleted stop in order ${props?.orderNo}`
            );
            toast.success(`Amount ${amountDifference.toFixed(2)} KD refunded to customer wallet`);
          }
        }
      } catch (error) {
        toast.error("Stop deleted but failed to update order amounts. Please recalculate manually.");
      }

    // Close the dialog and reset state after successful deletion
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    setStopToDelete(null);
  };

  const cancelDeleteStop = () => {
    setDeleteConfirmOpen(false);
    setStopToDelete(null);
  };

  return (
    <>
      <div className="flex-1 truncate">
        <div className="">
          <div className="card px-3 py-2 border border-light rounded-lg truncate">
            <div className="flex justify-between items-center border-b border-light pb-1 mb-2 truncate">
              <h2 className="font-bold text-base truncate">
                {title} {isMultiStopOrder && props?.type === "delivery" ? `(${dropoffStatuses.length > 0 ? dropoffStatuses.length : deliveryStops.length} stops)` : address?.name}
              </h2>
              {((props?.type === 'pickup' && props?.orderType === 'COURIER') || props.subStatus === "PAYMENT_PENDING") ? <></> :
                <div>
                  <button
                    type="button" 
                    onClick={() => {
                      if (canEditStop()) {
                        setAddressDialogOpen(true);
                        if(!saveAddresses?.length) getSavedAddressesById(props.userId);
                      }
                    }}
                    disabled={!canEditStop()}
                    className={`flex items-center ml-2 ${
                      canEditStop()
                        ? 'text-indigo-400 hover:text-indigo-300 cursor-pointer'
                        : 'text-gray-400 cursor-not-allowed opacity-50'
                    }`}
                    title={!canEditStop() ? 'Cannot edit: Order has been picked up' : 'Edit address'}
                  >
                    <span className="mr-1 text-xs">Edit</span>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd"></path></svg>
                  </button>
                </div>
              }
            </div>
            
            {/* Show multi-stop list or single address */}
            {isMultiStopOrder && props?.type === "delivery" && (dropoffStatuses.length > 0 || deliveryStops.length > 0) ? (
              <div className="flex flex-col gap-1.5 text-xs overflow-y-auto max-h-24">
                {(dropoffStatuses.length > 0 ? dropoffStatuses : deliveryStops).map((stop: any, index: number) => {
                  // If using dropoffStatuses, parse the customer_name
                  const parsedName = dropoffStatuses.length > 0 
                    ? parseCustomerName(stop.customer_name) 
                    : { name: stop.contactName, location: stop.name };
                  
                  const phoneNumber = dropoffStatuses.length > 0 
                    ? stop.mobile_number 
                    : stop.contactPhone;
                  
                  // Use array index position for stop number
                  const stopNumber = index + 1;

                  // Get location from stop - check both deliveryStops and dropoffStatuses
                  // For dropoffStatuses, location might be in parsedName.location or deliveryStops[index].name
                  // For deliveryStops, location is in stop.name
                  let displayLocation = parsedName.location;
                  if (!displayLocation && dropoffStatuses.length > 0 && deliveryStops[index]) {
                    displayLocation = deliveryStops[index].name;
                  }
                  if (!displayLocation && dropoffStatuses.length === 0) {
                    displayLocation = stop.name;
                  }
                  
                  return (
                    <div key={index} className="flex flex-col gap-0.5 pb-1.5 border-b border-gray-100 last:border-b-0 last:pb-0 relative group">
                      <div className="flex justify-between items-center gap-2">
                        {displayLocation && (
                          <div className="flex items-center flex-1 min-w-0">
                            <p className="w-20 font-semibold text-xs flex-shrink-0">Location</p>
                            :<span className="ml-1.5 text-xs truncate max-w-[200px]" title={displayLocation}>{displayLocation}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] font-semibold text-indigo-400 ${displayLocation ? 'ml-2' : ''}`}>Stop {stopNumber}</span>
                          {((props?.type === 'pickup' && props?.orderType === 'COURIER') || props.subStatus === "PAYMENT_PENDING") ? <></> : (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteClick(index, e)}
                              disabled={!canDeleteStop(stop, index)}
                              className={`p-1 rounded-full transition-colors ${
                                canDeleteStop(stop, index)
                                  ? 'bg-red-100 hover:bg-red-200 text-red-600 cursor-pointer'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                              }`}
                              title={getDeleteDisabledReason(stop, index)}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"></path>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <p className="w-20 font-semibold text-xs">Contact</p>
                        :<span className="ml-1.5 text-xs">{parsedName.name || props.customerName}</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-20 font-semibold text-xs">Phone</p>
                        :<span className="ml-1.5 text-xs cursor-pointer" onClick={() => {
                          (window as any)["_jorel-callPhoneNumber"](phoneNumber ?? props.customerPhone);
                        }}>{phoneNumber ?? props.customerPhone}</span>
                      </div>
                      {stop.primary_status !== undefined && (
                        <div className="flex items-center">
                          <p className="w-20 font-semibold text-xs">Status</p>
                          :<span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            getColorClasses(getStatusInfo(stop.primary_status).color)
                          }`}>
                            {getStatusInfo(stop.primary_status).label}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-2 justify-start items-start text-sm">
                <div className="flex items-center">
                  <p className="w-24 font-semibold">Contact name</p>
                  :<span className="ml-2">{address?.contactName ?? props.customerName}</span>
                </div>
                <div className="flex items-center">
                  <p className="w-24 font-semibold">Contact phone</p>
                  :<span className="ml-2 cursor-pointer" onClick={() => {
                    (window as any)["_jorel-callPhoneNumber"](address?.contactPhone ?? props.customerPhone);
                  }}>{address?.contactPhone ?? props.customerPhone}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {isEditAddressDialogOpen ? <Dialog open={isEditAddressDialogOpen} onClose={() => setAddressDialogOpen(false)} title={`Edit ${props?.type} address`}>
          <EditAddress 
            address={address} 
            deliveryStops={deliveryStops}
            isMultiStopOrder={isMultiStopOrder}
            otherAddress={props?.otherAddress} 
            title={title} 
            type={props?.type} 
            orderId={props?.orderId} 
            orderNo={props?.orderNo} 
            orderValidation={props?.orderValidation}
            deliveryNo={props?.deliveryNo} 
            orderType={props?.orderType} 
            fleetId={props?.fleetId} 
            userId={props?.userId} 
            primaryStatus={props?.primaryStatus}
            customerName={props?.customerName}
            customerPhone={props?.customerPhone}
          />
        </Dialog> : null}
        
        {/* Delete Confirmation Dialog */}
        {deleteConfirmOpen && (
          <Modal
            title="Confirm Delete Stop"
            onClose={cancelDeleteStop}
            width="min-w-[470px]"
          >
            <div className="bg-white p-4 -mx-6 -mb-6 -mt-2">
              <p className="text-sm mb-4">
                Are you sure you want to delete Stop {stopToDelete !== null ? stopToDelete + 1 : ''}?
              </p>
              <div className="flex justify-center items-center gap-2">
                <button
                  className="font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 p-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={cancelDeleteStop}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="font-semibold text-white bg-red-600 hover:bg-red-700 p-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  onClick={confirmDeleteStop}
                  disabled={isDeleting}
                >
                  {isDeleting && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </>
  )
}
